import { HeroUIProvider } from '@heroui/react';
import { ToastProvider } from '@heroui/toast';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { isAxiosError } from 'axios';

import { AuthProvider } from './firebase/AuthProvider.tsx';
import './firebase/firebase';
import { routeTree } from './routeTree.gen';

// Create a new QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
      retry: (failureCount, error) => {
        // A 4xx fails the same way next time, and a 429 means the API is already
        // out of instances — retrying it is load we put on an outage ourselves.
        const status = isAxiosError(error) ? error.response?.status : undefined;
        if (status != null && status >= 400 && status < 500) return false;
        return failureCount < 3;
      },
    },
  },
});

// Create a new router instance
const router = createRouter({ routeTree });

const showDevtools = import.meta.env.MODE !== 'prod';

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div className="flex min-h-screen items-center justify-center bg-gray-50">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">Something went wrong</h1>
            <p className="mt-2 text-gray-600">
              {error instanceof Error ? error.message : 'An unexpected error occurred'}
            </p>
            <button className="mt-4 rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700" onClick={resetError}>
              Try again
            </button>
          </div>
        </div>
      )}
    >
      <HeroUIProvider
        navigate={(to) => {
          const url = new URL(to, window.location.origin);
          router.navigate({
            to: url.pathname,
            search: Object.fromEntries(url.searchParams),
            hash: url.hash ? url.hash.slice(1) : undefined,
          });
        }}
        useHref={(href) => href}
      >
        <ToastProvider
          placement="bottom-right"
          maxVisibleToasts={5}
          disableAnimation
          toastOffset={16}
          toastProps={{
            radius: 'lg',
            timeout: 6000,
            shouldShowTimeoutProgress: false,
            classNames: {
              base: '!bg-white border border-default-100 shadow-lg min-w-[280px] max-w-[380px]',
              // The countdown paints a tinted fill behind the content; keep the card white
              progressTrack: 'hidden',
              progressIndicator: 'hidden',
              title: 'text-sm font-semibold text-default-900',
              description: 'text-[13px] text-default-600',
            },
          }}
        />
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <RouterProvider router={router} />
            {showDevtools && <ReactQueryDevtools initialIsOpen={false} />}
          </AuthProvider>
        </QueryClientProvider>
      </HeroUIProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
